<template>
  <div class="lesson-player-container">
    <!-- Top bar -->
    <div class="player-top-bar px-4 py-2 flex items-center gap-3">
      <v-btn icon dark small @click="$emit('back')">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <span class="text-white font-semibold text-sm truncate flex-1">{{ course.title }}</span>
      <v-chip dark small color="rgba(255,255,255,0.2)" class="font-semibold">
        <v-icon left x-small>mdi-book-check</v-icon>
        {{ completedCount }} / {{ course.lessons.length }} completed
      </v-chip>
    </div>

    <div class="player-body flex" style="height: calc(100vh - 160px);">
      <!-- Video area -->
      <div class="video-section flex-1 flex flex-col">
        <!-- Video Player -->
        <div class="video-wrapper bg-black flex items-center justify-center" style="min-height: 420px;">
        <!-- Video ready -->
        <div v-show="currentLesson && videoStatus === 'READY'" class="w-full relative">
          <video
            ref="videoPlayer"
            controls
            class="w-full"
            style="max-height: 480px; object-fit: contain;"
            @ended="onVideoEnded"
          >
            Your browser does not support the video tag.
          </video>
          <!-- Quality Selector Overlay -->
          <div v-if="qualities && qualities.length > 0" class="quality-selector-wrap absolute top-3 right-3 z-10">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  x-small
                  color="rgba(0, 0, 0, 0.7)"
                  dark
                  v-bind="attrs"
                  v-on="on"
                  class="normal-case font-bold"
                  style="border-radius: 4px;"
                >
                  <v-icon left x-small>mdi-settings</v-icon>
                  {{ currentQualityLabel }}
                </v-btn>
              </template>
              <v-list dense class="py-0">
                <v-list-item
                  v-for="q in qualities"
                  :key="q.label"
                  @click="changeQuality(q)"
                  :class="{ 'quality-active': currentQualityLabel === q.label }"
                >
                  <v-list-item-title class="text-xs">{{ q.label }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>

          <!-- Processing / polling -->
          <div v-if="currentLesson && videoStatus === 'PROCESSING'" class="text-center pa-8">
            <v-progress-circular indeterminate color="white" size="48" class="mb-4" />
            <p class="text-white text-sm font-medium mb-1">Video is being processed…</p>
            <p class="text-white text-xs opacity-60">Will check again in 30 seconds</p>
          </div>

          <!-- Loading stream URL -->
          <div v-else-if="currentLesson && videoStatus === 'loading'" class="text-center pa-8">
            <v-progress-circular indeterminate color="white" size="40" class="mb-3" />
            <p class="text-white text-sm opacity-70">Loading video…</p>
          </div>

          <!-- Error -->
          <div v-else-if="currentLesson && videoStatus === 'error'" class="text-center pa-8">
            <v-icon size="48" color="red lighten-2" class="mb-3">mdi-alert-circle-outline</v-icon>
            <p class="text-white text-sm">Failed to load video stream.</p>
            <v-btn x-small dark outlined class="mt-3 normal-case" @click="_loadStream">Retry</v-btn>
          </div>

          <!-- No lesson selected -->
          <div v-else-if="!currentLesson" class="text-white text-center pa-8">
            <v-icon size="64" color="white" class="mb-4">mdi-play-circle-outline</v-icon>
            <p class="text-lg">Select a lesson to begin</p>
          </div>
        </div>

        <!-- Lesson Info Area -->
        <div class="lesson-info-area flex-1 overflow-y-auto pa-5" v-if="currentLesson">
          <div class="flex items-start justify-between mb-3">
            <div>
              <h2 class="text-xl font-bold mb-1" style="color: #21427d;">{{ currentLesson.title }}</h2>
              <div class="flex items-center gap-3 text-sm text-gray-500">
                <span><v-icon x-small class="mr-1">mdi-clock-outline</v-icon>{{ currentLesson.duration }}</span>
                <span><v-icon x-small class="mr-1">mdi-book-outline</v-icon>Lesson {{ currentLesson.order }} of {{ course.lessons.length }}</span>
              </div>
            </div>
            <v-btn
              :color="isCurrentLessonCompleted ? '#4caf50' : '#21427d'"
              class="white--text normal-case rounded-lg"
              depressed
              small
              @click="toggleCompletion"
            >
              <v-icon left small>{{ isCurrentLessonCompleted ? 'mdi-check-circle' : 'mdi-check-circle-outline' }}</v-icon>
              {{ isCurrentLessonCompleted ? 'Completed' : 'Mark Complete' }}
            </v-btn>
          </div>

          <v-divider class="mb-4" />

          <p class="text-sm text-gray-700 leading-relaxed mb-5">{{ currentLesson.description }}</p>

          <!-- Prev/Next Navigation -->
          <div class="flex justify-between items-center">
            <v-btn
              outlined
              small
              class="normal-case"
              :disabled="currentLessonIndex <= 0"
              @click="goToLesson(currentLessonIndex - 1)"
              color="#21427d"
            >
              <v-icon left small>mdi-skip-previous</v-icon>
              Previous Lesson
            </v-btn>
            <v-btn
              outlined
              small
              class="normal-case"
              :disabled="currentLessonIndex >= course.lessons.length - 1"
              @click="goToLesson(currentLessonIndex + 1)"
              color="#21427d"
            >
              Next Lesson
              <v-icon right small>mdi-skip-next</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Right Sidebar - Lesson List -->
      <div class="lesson-sidebar" style="width: 340px; border-left: 1px solid #e0e0e0;">
        <div class="sidebar-header px-4 py-3 border-b" style="background: #f5f7fa;">
          <div class="font-bold text-sm mb-1" style="color: #21427d;">Course Content</div>
          <v-progress-linear
            :value="progressPercent"
            color="#21427d"
            height="6"
            rounded
            background-color="#e0e0e0"
          />
          <div class="text-xs text-gray-500 mt-1">{{ progressPercent }}% complete</div>
        </div>

        <div class="lesson-list-scroll overflow-y-auto" style="height: calc(100% - 80px);">
          <div
            v-for="(lesson, index) in course.lessons"
            :key="lesson._id"
            class="lesson-list-item px-4 py-3 flex items-start gap-3 cursor-pointer border-b"
            :class="{
              'active-lesson': currentLesson && currentLesson._id === lesson._id,
              'completed-lesson': isLessonDone(lesson._id)
            }"
            @click="goToLesson(index)"
          >
            <!-- Completion / Play indicator -->
            <div class="flex-shrink-0 mt-1">
              <v-icon
                v-if="isLessonDone(lesson._id)"
                small
                color="#4caf50"
              >mdi-check-circle</v-icon>
              <v-icon
                v-else-if="currentLesson && currentLesson._id === lesson._id"
                small
                color="#21427d"
              >mdi-play-circle</v-icon>
              <v-icon v-else small color="#bdbdbd">mdi-play-circle-outline</v-icon>
            </div>

            <div class="flex-1 min-w-0">
              <div
                class="text-sm font-medium truncate"
                :class="{
                  'font-bold': currentLesson && currentLesson._id === lesson._id,
                  'text-gray-400': false
                }"
                :style="{ color: currentLesson && currentLesson._id === lesson._id ? '#21427d' : '' }"
              >
                {{ index + 1 }}. {{ lesson.title }}
              </div>
              <div class="text-xs text-gray-400 mt-0.5">
                <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>{{ lesson.duration }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Hls from 'hls.js'
import {
  isLessonCompleted,
  markLessonComplete,
  markLessonIncomplete,
  getCourseProgress,
  getCompletedLessonsCount,
  state,
  initLearningData
} from './learningData'

export default {
  name: 'LessonPlayer',
  props: {
    course: { type: Object, required: true },
    initialLessonId: { type: String, default: null },
    currentUserId: { type: String, default: 'u1' }
  },
  data() {
    return {
      currentLessonIndex: 0,
      videoStatus: null,   // null | 'loading' | 'PROCESSING' | 'READY' | 'error'
      qualities: [],
      currentQualityLabel: '1080p'
    }
  },
  computed: {
    currentLesson() {
      return this.course.lessons[this.currentLessonIndex] || null
    },
    isCurrentLessonCompleted() {
      if (!this.currentLesson) return false
      // Access state.progress to ensure reactivity
      void state.progress.length
      return isLessonCompleted(this.course._id, this.currentLesson._id, this.currentUserId)
    },
    completedCount() {
      void state.progress.length
      return getCompletedLessonsCount(this.course._id, this.currentUserId)
    },
    progressPercent() {
      void state.progress.length
      return getCourseProgress(this.course._id, this.currentUserId)
    }
  },
  created() {
    if (this.initialLessonId) {
      const idx = this.course.lessons.findIndex(l => l._id === this.initialLessonId)
      if (idx > -1) this.currentLessonIndex = idx
    }
  },
  mounted() {
    if (this.currentLesson) this._loadStream()
  },
  watch: {
    currentLesson(lesson) {
      this._clearPoll()
      this.videoStatus = null
      this.$nextTick(() => {
        if (lesson) this._loadStream()
      })
    }
  },
  beforeDestroy() {
    this._clearPoll()
    this._destroyHls()
  },
  methods: {
    isLessonDone(lessonId) {
      void state.progress.length
      return isLessonCompleted(this.course._id, lessonId, this.currentUserId)
    },
    goToLesson(index) {
      if (index >= 0 && index < this.course.lessons.length) {
        this.currentLessonIndex = index
      }
    },
    async toggleCompletion() {
      if (!this.currentLesson) return
      if (this.isCurrentLessonCompleted) {
        await markLessonIncomplete(this.$axios, this.course._id, this.currentLesson._id, this.currentUserId)
      } else {
        await markLessonComplete(this.$axios, this.course._id, this.currentLesson._id, this.currentUserId)
      }
      await initLearningData(this.$axios)
    },
    async onVideoEnded() {
      // Auto-mark as complete
      if (this.currentLesson && !this.isCurrentLessonCompleted) {
        await markLessonComplete(this.$axios, this.course._id, this.currentLesson._id, this.currentUserId)
        await initLearningData(this.$axios)
      }
      // Auto-advance to next lesson
      if (this.currentLessonIndex < this.course.lessons.length - 1) {
        this.goToLesson(this.currentLessonIndex + 1)
      }
    },

    // ── Stream loader — calls the backend presign API, polls if processing ──
    async _loadStream() {
      const lesson = this.currentLesson
      console.log("[LessonPlayer] _loadStream started for lesson:", lesson)
      if (!lesson) {
        console.log("[LessonPlayer] No current lesson found.")
        return
      }

      this._clearPoll()
      this._destroyHls()

      // No backend video yet — fall back to sample MP4
      if (!lesson.contentId) {
        console.log("[LessonPlayer] Lesson has no contentId. Falling back to sample video URL:", lesson.videoUrl)
        this.videoStatus = 'READY'
        await this.$nextTick()
        this._attachSrc(lesson.videoUrl)
        return
      }

      console.log("[LessonPlayer] Lesson has contentId:", lesson.contentId, "Fetching stream info...")
      this.videoStatus = 'loading'

      try {
        const courseId = this.course.rawCourse?._id || this.course._id
        const targetUrl = `/learning_center/video/stream/${courseId}/${lesson.contentId}`
        console.log("[LessonPlayer] GET Requesting URL:", targetUrl)
        const res = await this.$axios.get(targetUrl)
        const data = res.data
        console.log("[LessonPlayer] API response data:", data)

        if (data.hlsStatus !== 'READY') {
          console.log("[LessonPlayer] Video not ready yet. Status:", data.hlsStatus, "Scheduling polling...")
          this.videoStatus = 'PROCESSING'
          // Poll every 30s
          this._pollTimer = setTimeout(() => this._loadStream(), 30000)
          return
        }

        console.log("[LessonPlayer] Stream READY. signed URL:", data.streamUrl)
        this.qualities = data.qualities || []
        this.currentQualityLabel = this.qualities.length > 0 ? this.qualities[0].label : '1080p'
        this.videoStatus = 'READY'
        await this.$nextTick()
        this._attachHls(data.streamUrl)
      } catch (err) {
        console.error('[LessonPlayer] Stream load error:', err)
        this.videoStatus = 'error'
      }
    },

    changeQuality(quality) {
      if (this.currentQualityLabel === quality.label) return
      this.currentQualityLabel = quality.label
      const video = this.$refs.videoPlayer
      if (!video) return

      const currentTime = video.currentTime
      const isPaused = video.paused

      this._destroyHls()
      this._attachHls(quality.url)

      // Resume playback at the same timestamp
      this.$nextTick(() => {
        video.currentTime = currentTime
        if (!isPaused) {
          video.play().catch(e => console.log('Autoplay play interrupted:', e))
        }
      })
    },

    _attachHls(url) {
      const video = this.$refs.videoPlayer
      if (!video) return
      if (Hls.isSupported()) {
        this._hls = new Hls()
        this._hls.loadSource(url)
        this._hls.attachMedia(video)
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari native HLS
        video.src = url
      }
    },

    _attachSrc(url) {
      this.qualities = []
      this.currentQualityLabel = 'Default'
      const video = this.$refs.videoPlayer
      if (video) video.src = url
    },

    _clearPoll() {
      if (this._pollTimer) {
        clearTimeout(this._pollTimer)
        this._pollTimer = null
      }
    },

    _destroyHls() {
      if (this._hls) {
        this._hls.destroy()
        this._hls = null
      }
    }
  }
}
</script>

<style scoped>
.player-top-bar {
  background: linear-gradient(135deg, #21427d 0%, #3a6bc5 100%);
  min-height: 48px;
}

.lesson-list-item {
  transition: background-color 0.2s ease;
}
.lesson-list-item:hover {
  background-color: #f0f4ff;
}
.active-lesson {
  background-color: #e8eef8 !important;
  border-left: 3px solid #21427d;
}
.completed-lesson {
  background-color: #f1f8e9;
}
.completed-lesson.active-lesson {
  background-color: #e8eef8 !important;
}

.lesson-info-area {
  background: #fafbfd;
}
</style>
